import React, { useCallback, useContext, useRef, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AttentionBox as AttentionBoxBase, FormModal as BaseFormModal } from '@ubeya/shared-web/components';
import { getRichTextVisibleLength } from '@ubeya/shared-web/components/RichTextEditor/markdownUtils';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import { SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFields, { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { getDirtyFields } from '@ubeya/shared/utils/form';
import PositionModalFields from './PositionsModalFields';
import PositionComplianceModal from './Compliance/PositionComplianceModal';
import { JOB_DESCRIPTION_MAX_LENGTH } from 'settings/constants';

const FormModal = styled(BaseFormModal)`
  #modal-container {
    padding: 0;
    position: relative;
  }
`;

const AttentionBox = styled(AttentionBoxBase)`
  margin: 20px 20px 0 20px;
`;

const formatWage = (item) => ({
  ...item,
  wage: {
    hourlyWage: item.wage.hourlyWage === '' ? undefined : item.wage.hourlyWage,
    flatWage: item.wage.flatWage === '' ? undefined : item.wage.flatWage,
    flatHours: item.wage.flatHours === '' ? undefined : item.wage.flatHours
  },
  clientWage: {
    hourlyWage: item.clientWage.hourlyWage === '' ? undefined : item.clientWage.hourlyWage,
    flatWage: item.clientWage.flatWage === '' ? undefined : item.clientWage.flatWage,
    flatHours: item.clientWage.flatHours === '' ? undefined : item.clientWage.flatHours
  }
});

const PositionModal = ({ selectedPosition, onClose, onConfirm, className }) => {
  const { t } = useContext(ConfigContext);
  const [complianceModal, setComplianceModal] = useState(null);
  const [complianceChanged, setComplianceChanged] = useState(false);

  const complianceRef = useRef();

  //scroll to bottom when updating compliance fields
  useEffect(() => {
    if (!complianceChanged) {
      return;
    }
    complianceRef && complianceRef.current && complianceRef.current.scrollIntoView({ behavior: 'smooth' });

    setComplianceChanged(false);
  }, [complianceChanged]);

  const { account } = useAccountConfig();
  const { editPosition, addPosition } = usePositions();
  const { fields: employeeFields, addField } = useFields();

  const isFlatWage = account?.payrollConfig?.flatIsActive;

  const fields = useMemo(
    () =>
      employeeFields.filter(({ fieldTypeId, name }) => {
        if (['birthday', 'birthdate', 'start-employment-date'].includes(name)) {
          return false;
        }
        return [FieldTypes.FIELD_TYPE_DOCUMENT, FieldTypes.FIELD_TYPE_OPTIONS, FieldTypes.FIELD_TYPE_DATE].includes(
          fieldTypeId
        );
      }),
    [employeeFields]
  );

  const mappedFields = useMemo(
    () =>
      (fields || []).reduce((all, curr) => {
        return { ...all, [curr.id]: curr };
      }, {}),
    [fields]
  );

  const initialComplianceFieldsIds = selectedPosition?.compliance?.fieldsIds;
  const initialComplianceFieldsValues = selectedPosition?.compliance?.fieldsValues;

  const initialValues = useMemo(() => {
    const fieldsIds = (fields || [])
      .map(({ id }) => id)
      .reduce((all, fieldId) => {
        all[`field-${fieldId}`] = (initialComplianceFieldsIds || []).includes(fieldId);
        return all;
      }, {});
    const fieldsValues = Object.fromEntries(
      Object.entries(initialComplianceFieldsValues || {}).map(([key, value]) => [`field-${key}`, value])
    );

    return {
      id: selectedPosition?.id ?? selectedPosition?.slug,
      wages: selectedPosition?.wages,
      slug: t(selectedPosition?.prevSlug),
      jobDescription: selectedPosition?.jobDescription,
      paycode: selectedPosition?.paycode ?? '',
      branchIds: selectedPosition?.id != null ? selectedPosition.branchIds : undefined,
      hourlyWage: selectedPosition?.wage?.hourlyWage ?? 0,
      flatWage: selectedPosition?.wage?.flatWage ?? 0,
      flatHours: selectedPosition?.wage?.flatHours ?? 0,
      clientHourlyWage: selectedPosition?.clientWage?.hourlyWage ?? 0,
      clientFlatWage: selectedPosition?.clientWage?.flatWage ?? 0,
      clientFlatHours: selectedPosition?.clientWage?.flatHours ?? 0,
      compliance: {
        fieldsIds,
        fieldsValues,
        blockBooking: !!selectedPosition?.compliance?.blockBooking
      }
    };
  }, [
    fields,
    initialComplianceFieldsValues,
    selectedPosition?.id,
    selectedPosition?.slug,
    selectedPosition?.wages,
    selectedPosition?.prevSlug,
    selectedPosition?.jobDescription,
    selectedPosition?.paycode,
    selectedPosition?.branchIds,
    selectedPosition?.wage?.hourlyWage,
    selectedPosition?.wage?.flatWage,
    selectedPosition?.wage?.flatHours,
    selectedPosition?.clientWage?.hourlyWage,
    selectedPosition?.clientWage?.flatWage,
    selectedPosition?.clientWage?.flatHours,
    selectedPosition?.compliance?.blockBooking,
    t,
    initialComplianceFieldsIds
  ]);

  const handleSubmit = useCallback(
    async (values, form) => {
      const { dirtyFields } = form.getState();

      // generate dirty fields for wages
      const dirtyValues = getDirtyFields(values, {
        wages: dirtyFields.wages
      });

      const complianceFieldsIds = values?.compliance
        ? Object.entries(values?.compliance?.fieldsIds)
            .filter(([, value]) => !!value)
            .map(([key]) => Number(key.split('-')[1]))
        : undefined;
      const complianceFieldsValues = values?.compliance?.fieldsValues
        ? Object.fromEntries(
            Object.entries(values?.compliance?.fieldsValues)
              .filter(([, value]) => !!value)
              .map(([key, value]) => [Number(key.split('-')[1]), value])
          )
        : undefined;

      const editedPositionCompliance = {
        fieldsIds: complianceFieldsIds,
        fieldsValues: complianceFieldsValues,
        blockBooking: complianceFieldsIds?.length > 0 ? values?.compliance?.blockBooking : false
      };
      const addedPositionCompliance =
        complianceFieldsIds?.length > 0
          ? {
              fieldsIds: complianceFieldsIds,
              fieldsValues: complianceFieldsValues,
              blockBooking: complianceFieldsIds?.length > 0 ? values?.compliance?.blockBooking : false
            }
          : undefined;

      let wages = dirtyValues.wages || undefined;

      if (wages) {
        wages.added = wages.added.map(formatWage);
        wages.edited = wages.edited.map(formatWage);
      }

      const wage =
        dirtyFields.hourlyWage || dirtyFields.flatWage || dirtyFields.flatHours
          ? {
              hourlyWage: values.hourlyWage,
              flatWage: values.flatWage,
              flatHours: values.flatHours
            }
          : undefined;

      const clientWage =
        dirtyFields.clientHourlyWage || dirtyFields.clientFlatWage || dirtyFields.clientFlatHours
          ? {
              hourlyWage: values.clientHourlyWage,
              flatWage: values.clientFlatWage,
              flatHours: values.clientFlatHours
            }
          : undefined;

      const wageItems = {
        wages,
        clientWage,
        wage
      };

      if (selectedPosition?.id) {
        await editPosition({
          id: selectedPosition.id,
          slug: values.slug,
          jobDescription: values.jobDescription || '',
          branchIds: values.branchIds ?? [],
          paycode: values.paycode || null,
          compliance: editedPositionCompliance,

          // WAGES ITEMS
          ...wageItems
        });
        onConfirm?.(selectedPosition.id);
      } else {
        const { id } = await addPosition({
          slug: values.id,
          jobDescription: values.jobDescription,
          branchIds: values.branchIds ?? [],
          paycode: values.paycode || null,
          compliance: addedPositionCompliance,

          // WAGES ITEMS
          ...wageItems
        });
        onConfirm?.(id);
      }
      onClose();
    },
    [addPosition, editPosition, onClose, onConfirm, selectedPosition]
  );

  const [addedPositions, setAddedPosition] = useState(() =>
    !selectedPosition?.id && selectedPosition?.slug
      ? [{ value: selectedPosition?.slug, label: selectedPosition?.slug }]
      : []
  );

  const options = useMemo(() => {
    return addedPositions.sort(SORT_BY_STRING('label'));
  }, [addedPositions]);

  const validate = useCallback(
    (values) => {
      const { jobDescription = '' } = values;
      const errors = {};

      if (getRichTextVisibleLength(jobDescription) > JOB_DESCRIPTION_MAX_LENGTH) {
        errors.jobDescription = t('tooManyCharactersInJobDescriptionErrorBody');
      }
      return errors;
    },
    [t]
  );

  return (
    <FormModal
      className={className}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      size="sLong"
      onClose={onClose}
      validate={validate}
      title={selectedPosition?.id ? t('editPosition') : t('addPosition')}
      confirmText={selectedPosition?.id ? t('update') : t('add')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnInvalid
      disabledOnPristine={false}>
      <>
        <AttentionBox> {t('positionAttentionBoxInfo')} </AttentionBox>

        <PositionModalFields
          options={options}
          setComplianceModal={setComplianceModal}
          isFlatWage={isFlatWage}
          mappedFields={mappedFields}
          setAddedPosition={setAddedPosition}
          selectedPosition={selectedPosition}
        />

        {complianceModal && (
          <PositionComplianceModal
            setComplianceChanged={setComplianceChanged}
            mappedFields={mappedFields}
            addField={addField}
            t={t}
            onClose={() => setComplianceModal(false)}
          />
        )}
        <div ref={complianceRef} />
      </>
    </FormModal>
  );
};

export default PositionModal;
